import { useDisclosure } from "@shared/hooks/useDisclosure";
import useNavigationRoutes from "@shared/hooks/navigation/useNavigationRoutes";
import { useUserSessionAuthState } from "@shared/modules/auth/contexts/useUserSessionAuthState";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import { useTranslation } from "@shared/utils/internationalization";
import { useContextualRouting } from "next-use-contextual-routing";
import { useRouter } from "next/router";
import { useCallback } from "react";

export const useNavBarActions = () => {
  const router = useRouter();
  const { t } = useTranslation(["common", "account"]);
  const { getLoggedInNavBarLinks, loggedOutLinks } = useNavigationRoutes();
  const { makeContextualHref } = useContextualRouting();
  const { logout, userProfileData: user } = useUserSessionAuthState();

  const {
    isOpen: isMobileMenuOpen,
    onOpen: mobileMenuOnOpen,
    onClose: mobileMenuOnClose,
  } = useDisclosure();

  const loggedInLinks = user
    ? getLoggedInNavBarLinks(user).map((link) => {
        return { ...link, label: t(link.label, { ns: ["common", "account"] }) };
      })
    : [];

  const goToLocale = useCallback(
    (localeRoute: string) => {
      router.push(router.asPath, router.asPath, { locale: localeRoute });
    },
    [router]
  );

  const logInOnClick = useCallback(() => {
    mobileMenuOnClose();
    router.push(
      makeContextualHref({
        accountModal: "login",
      }),
      "/accounts/login/",
      {
        shallow: true,
      }
    );
  }, [mobileMenuOnClose, router, makeContextualHref]);

  const signUpOnClick = useCallback(() => {
    mobileMenuOnClose();
    router.push(
      makeContextualHref({
        accountModal: "createAccount",
      }),
      "/accounts/signup/",
      {
        shallow: true,
      }
    );
  }, [mobileMenuOnClose, router, makeContextualHref]);

  const handleLogout = useCallback(async () => {
    try {
      logout();
      mobileMenuOnClose();
    } catch (error) {
      recordAppError(error);
    }
  }, [logout, mobileMenuOnClose]);

  return {
    loggedInLinks,
    loggedOutLinks,
    isMobileMenuOpen,
    mobileMenuOnOpen,
    mobileMenuOnClose,
    goToLocale,
    logInOnClick,
    signUpOnClick,
    handleLogout,
  };
};
